import { pushToDataLayer } from '../../common/analytics';
import { getAddToCartAnalyticsEvent } from '../../common/utils/analyticsUtils';
import { getCampaignPromotion, getOfferCampaignAssociationAndPromotion } from '../../common/utils/campaignUtils';
import { SalesType } from '../../generated/api/salesType';
import { getSelectedServiceFee } from '../OrderSubscriptionSelection/OrderSubscriptionSelectionUtils';
import { getAddedAddOns } from '../SubscriptionDetails/addOnDependencyUtils';
import { getLaitenettiOfferByName, getOffer, getOfferByCommercialProductCode } from './SubscriptionUtils';
import type { AddOnRulesResponse } from '../../generated/api/addOnRulesResponse';
import type { CampaignContext } from '../../generated/api/campaignContext';
import type { CampaignPromotion } from '../../generated/api/campaignPromotion';
import type { Offer } from '../../generated/api/offer';
import type { OnlineModel } from '../../generated/api/onlineModel';
import type { Subscription } from '../../generated/api/subscription';
import type { AssociationRecord } from '../../legacy/cloudsense-add-on-dependency-engine/AddOnManagerOnline';
import type { SubscriptionUpdateNavigationState } from '../../public/site/path/SelfService/ProductsAndServices/MobileSubUpdateConfirmationPath';
import type { OfferProductMapType } from '../OrderSubscriptionSelection/OrderSubscriptionSelectionUtils';
import type { NavigateFunction } from 'react-router';

interface ChangeOfferHandlerOptions {
  onlineModel: OnlineModel;
  navigate: NavigateFunction;
  search: string;

  quantity?: number;
  voucherCode?: string;
  services?: Array<{ isIncluded?: boolean; guid?: string; applicableOfferGuid?: string }>;
  selectedAddonCodes?: string[];
  offerAndFeeMap?: OfferProductMapType[];

  campaignsFromVoucher: CampaignPromotion[];
  campaignContextsFromVoucher: CampaignContext[];

  laitenettiOfferName?: string; // For laitenetti
  noeOfferCode?: string; // For mobile broadband
  offers?: Array<{ guid?: string }>; // For voice subscriptions

  additionalOffers?: Offer[];
  addOnRules?: AddOnRulesResponse;
  confirmationPath: string;
  subscription?: Subscription;
}

export const handleChangeOffer = ({
  onlineModel,
  navigate,
  search,
  quantity = 1,
  voucherCode,
  services = [],
  selectedAddonCodes = [],
  offerAndFeeMap = [],
  campaignsFromVoucher = [],
  campaignContextsFromVoucher = [],
  noeOfferCode,
  laitenettiOfferName,
  offers,
  additionalOffers = [],
  addOnRules,
  subscription,
  confirmationPath,
}: ChangeOfferHandlerOptions) => {
  // Get the appropriate offer
  const allOffers = [...(onlineModel.offers ?? []), ...additionalOffers];

  // Determine which offer code to use based on what's provided
  const offerToFind = noeOfferCode ?? offers?.[0]?.guid;

  const offer = laitenettiOfferName
    ? getLaitenettiOfferByName(laitenettiOfferName, allOffers)
    : getOffer(offerToFind, allOffers);

  if (!offer) {
    return;
  }

  const selectedServiceFee = getSelectedServiceFee(offerAndFeeMap, offer.commercialProducts[0].commercialProductId);

  // Handle campaign associations and promotions
  const campaignPromotions = [...campaignsFromVoucher, ...(onlineModel.campaigns || [])];
  const offerCampaignAssociationAndPromotion = getOfferCampaignAssociationAndPromotion(
    offer,
    SalesType.CHANGE_OFFER,
    campaignContextsFromVoucher,
    voucherCode,
    campaignPromotions
  );

  const offerCampaignAssociation = offerCampaignAssociationAndPromotion?.campaignAssociation;
  let campaignDescription: string | undefined;

  if (offerCampaignAssociation) {
    const campaignPromotion = getCampaignPromotion(campaignPromotions, offerCampaignAssociation.campaignPromotionCode);
    campaignDescription = campaignPromotion?.description;
  }

  // Push analytics data
  pushToDataLayer(
    getAddToCartAnalyticsEvent(
      onlineModel.onlineModelName,
      offer,
      quantity,
      [], // Addons will be added later
      voucherCode,
      offerCampaignAssociation
    )
  );

  // Process included services
  const includedServices =
    services
      ?.filter(s => s.isIncluded && s.guid !== undefined)
      .filter(s => s.applicableOfferGuid === undefined || s.applicableOfferGuid === noeOfferCode)
      .map(s => s.guid as string) ?? [];

  // Get add-ons if addOnRules are provided
  const addOnAssociations = addOnRules?.associations?.[0] as AssociationRecord[];

  let selectedAddons = undefined;

  if (addOnAssociations) {
    if (selectedAddonCodes && selectedAddonCodes?.length > 0) {
      selectedAddons = getAddedAddOns(selectedAddonCodes, offer, addOnAssociations);
    } else {
      selectedAddons = getAddedAddOns(includedServices, offer, addOnAssociations);
    }
  }

  // Navigate based on whether this is a change offer or not
  navigate(confirmationPath + search, {
    state: {
      selectedAddOns: selectedAddons,
      selectedCommercialProduct: { ...offer.commercialProducts[0], oneTimeCharge: 0 },
      subscription: subscription,
      campaignAssociation: offerCampaignAssociation,
      campaignDescription: campaignDescription,
      selectedServiceFee,
      onlineModelName: onlineModel.onlineModelName,
      offer: offer,
      previousOffer: getOfferByCommercialProductCode(allOffers, subscription?.commercialProductCode),
      voucher: voucherCode,
    } as SubscriptionUpdateNavigationState,
  });
};
